import { ToolDialog, ToolSummary, ToolSearch, ToolButton, ToolEmpty, ToolFilters } from "./ToolWorkspace";
import { HeaderIcon } from "./AppHeader";
import { useState, useEffect } from "react";

import { toast } from "../hooks/useToast";

import { CORES_PRESET } from "../constants/colors";
import { fbList, fbAdd, fbUpdate, fbDelete } from "../hooks/useFirebase";
import { useConfirm } from "../hooks/useConfirm";
import { Inp } from "./ui/Inp";
import { Sel } from "./ui/Sel";

import { CorSelector } from "./CorSelector";
import { MarcaSelector } from "./MarcaSelector";

const COR_HEX_FALLBACK = {
  preto:"#1a1a1a", negro:"#1a1a1a", black:"#111",
  branco:"#f0f0f0", white:"#f0f0f0",
  cinza:"#808080", gray:"#808080", grey:"#808080", prata:"var(--pc-ui-94a3b8, #94a3b8)", silver:"var(--pc-ui-94a3b8, #94a3b8)",
  vermelho:"#dc2626", red:"#dc2626", vinho:"#9f1239",
  azul:"#2563eb", blue:"#2563eb", ciano:"#06b6d4", cyan:"#06b6d4", turquesa:"#2dd4bf",
  verde:"#16a34a", green:"#16a34a", lima:"#84cc16",
  amarelo:"#eab308", yellow:"#eab308",
  laranja:"#ea580c", orange:"#ea580c", coral:"#f97316",
  roxo:"#7c3aed", purple:"#7c3aed", violeta:"#8b5cf6", lilas:"#a78bfa", magenta:"#d946ef",
  rosa:"#ec4899", pink:"#ec4899",
  marrom:"#92400e", brown:"#92400e", bege:"#d4b483", natural:"#f5e6c8",
  cobre:"#b45309", copper:"#b45309", dourado:"var(--pc-ui-f59e0b, #f59e0b)", gold:"var(--pc-ui-f59e0b, #f59e0b)",
  transparente:"#bae6fd",
};

function corFilamentoHex(corNome, coresCustom) {
  const c = [...CORES_PRESET, ...(coresCustom || [])].find(c => c.nome === corNome);
  if (c?.hex) return c.hex;
  const lower = (corNome || "").toLowerCase().trim();
  for (const [k, v] of Object.entries(COR_HEX_FALLBACK)) {
    if (lower.includes(k)) return v;
  }
  return "#64748b";
}

export function Filamentos({ coresCustom, onAddCor, marcasSalvas, onAddMarca }) {
  const empty = { material:"PLA", cor:"", quantidade:"1000", custo:"", marca:"" };
  const [data, setData] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [confirmar, modalConfirm] = useConfirm();
  const [filtroMat, setFiltroMat] = useState("Todos");
  const [mostrarForm, setMostrarForm] = useState(false);
  const [busca, setBusca] = useState("");
  const tiposMat = ["PLA","PETG","ABS","TPU","ASA","Nylon","Resin","Outro"];

  const emptySaida = { quantidade:"", motivo:"Falha de impressão", obs:"" };
  const [saidaAlvo, setSaidaAlvo] = useState(null);
  const [saidaForm, setSaidaForm] = useState(emptySaida);
  const [salvandoSaida, setSalvandoSaida] = useState(false);
  const motivosSaida = ["Falha de impressão", "Teste/calibração", "Uso pessoal", "Amostra/Brinde", "Ajuste de estoque", "Outro"];

  const carregar = async (silencioso=true) => {
    if (!silencioso) setLoading(true);
    try { setData(await fbList("filamentos")); }
    catch(e) { toast("Erro: "+e.message, "error"); }
    if (!silencioso) setLoading(false);
  };
  useEffect(() => { carregar(false); }, []);

  const fecharForm = () => { setMostrarForm(false); setEditId(null); setForm(empty); };

  const save = async () => {
    if (salvando) return;
    if (!Number.isFinite(Number(form.quantidade)) || Number(form.quantidade) < 0 || form.quantidade === "") { toast("Informe uma quantidade válida em gramas.", "warning"); return; }
    if (!form.cor) { toast("Selecione uma cor!", "warning"); return; }
    setSalvando(true);
    const nome = [form.marca, form.material, form.cor].filter(Boolean).join(" ");
    const qtdG = parseFloat(form.quantidade) || 0;
    const custo = parseFloat(form.custo) || null;
    const norm = (s) => (s||"").trim().toLowerCase();
    try {
      if (editId) {
        await fbUpdate("filamentos", editId, {
          nome, material:form.material, cor:form.cor,
          peso: qtdG, peso_restante: qtdG,
          custo, marca:form.marca||null,
        });
      } else {
        // Se já existe filamento com mesma cor + marca + material, soma ao estoque existente
        const existente = data.find(f =>
          norm(f.cor) === norm(form.cor) &&
          norm(f.marca) === norm(form.marca) &&
          norm(f.material) === norm(form.material)
        );
        if (existente) {
          await fbUpdate("filamentos", existente.id, {
            peso: (parseFloat(existente.peso)||0) + qtdG,
            peso_restante: (parseFloat(existente.peso_restante)||0) + qtdG,
            custo: custo ?? existente.custo ?? null,
          });
          toast("Filamento já existe — estoque somado ao cadastro existente!", "success");
        } else {
          await fbAdd("filamentos", {
            nome, material:form.material, cor:form.cor,
            peso: qtdG, peso_restante: qtdG,
            custo, marca:form.marca||null,
          });
        }
      }
      fecharForm();
      carregar();
    } catch(e) { toast("Erro ao salvar: "+e.message, "error"); }
    setSalvando(false);
  };

  const del = async (id) => {
    if (await confirmar("Excluir filamento?")) { await fbDelete("filamentos", id); carregar(); }
  };

  const abrirSaida = (f) => { setSaidaAlvo(f); setSaidaForm(emptySaida); };
  const fecharSaida = () => { setSaidaAlvo(null); setSaidaForm(emptySaida); };

  const confirmarSaida = async () => {
    const qtd = parseFloat(saidaForm.quantidade);
    if (!qtd || qtd <= 0) { toast("Informe uma quantidade válida!", "warning"); return; }
    const restanteAtual = parseFloat(saidaAlvo.peso_restante) || 0;
    if (qtd > restanteAtual) {
      const ok = await confirmar(`Só há ${restanteAtual}g em estoque. Registrar saída de ${qtd}g mesmo assim (zera o estoque)?`);
      if (!ok) return;
    }
    setSalvandoSaida(true);
    try {
      const novoRestante = Math.max(0, restanteAtual - qtd);
      await fbUpdate("filamentos", saidaAlvo.id, { peso_restante: novoRestante });
      await fbAdd("saidas_filamento", {
        filamento_id: saidaAlvo.id,
        nome: saidaAlvo.nome || [saidaAlvo.marca, saidaAlvo.material, saidaAlvo.cor].filter(Boolean).join(" "),
        material: saidaAlvo.material || null,
        cor: saidaAlvo.cor || null,
        marca: saidaAlvo.marca || null,
        quantidade: qtd,
        motivo: saidaForm.motivo,
        observacao: saidaForm.obs || null,
        peso_restante_apos: novoRestante,
      });
      toast("Saída registrada!", "success");
      fecharSaida();
      carregar();
    } catch(e) { toast("Erro ao registrar saída: "+e.message, "error"); }
    setSalvandoSaida(false);
  };

  const edit = (f) => {
    setForm({
      material:f.material||"PLA", cor:f.cor||"",
      quantidade: f.peso_restante!=null ? String(parseFloat(f.peso_restante)) : "",
      custo:f.custo||"", marca:f.marca||"",
    });
    setEditId(f.id);
    setMostrarForm(true);
  };

  const filtrado = (filtroMat === "Todos" ? data : data.filter(f => f.material === filtroMat))
    .filter(f => !busca.trim() || (f.nome || [f.marca,f.material,f.cor].filter(Boolean).join(" ")).toLowerCase().includes(busca.toLowerCase()));

  const peso = f => Number(f.peso_restante ?? f.peso ?? 0);
  return <div className="tool-module tool-module--filamentos">
    {modalConfirm}
    <ToolSummary items={[{label:"Filamentos",value:loading?"—":data.length},{label:"Em estoque",value:(data.reduce((n,f)=>n+peso(f),0)/1000).toLocaleString("pt-BR",{maximumFractionDigits:2})+" kg"},{label:"Até 100 g",value:data.filter(f=>peso(f)<=100).length}]} />
    <ToolSearch value={busca} onChange={setBusca} placeholder="Pesquisar filamento"><ToolButton data-tour="filamentos-novo" onClick={()=>setMostrarForm(true)}>+ Novo filamento</ToolButton></ToolSearch>
    {!loading&&data.length>0&&<ToolFilters value={filtroMat} onChange={setFiltroMat} options={["Todos",...tiposMat.filter(m=>data.some(f=>f.material===m))].map(value=>({value,label:value,count:value==="Todos"?data.length:data.filter(f=>f.material===value).length}))} />}
    <div className="tool-item-grid" data-tour="filamentos-lista" data-count={data.length}>
      {loading?<ToolEmpty icon="filamentos" title="Carregando filamentos…" />:!filtrado.length?<ToolEmpty icon="filamentos" title={data.length?"Nenhum filamento encontrado":"Escolha a primeira cor da sua coleção"}>{data.length?"Ajuste a busca ou o material selecionado.":"Cadastre material, cor e custo para começar a calcular suas peças."}</ToolEmpty>:filtrado.map(f=><article key={f.id} className="tool-item">
        <div className="tool-item-top"><span className="tool-spool" style={{"--spool-color":corFilamentoHex(f.cor,coresCustom)}}><HeaderIcon name="filamentos" size={32} /></span><div className="tool-item-copy"><h3>{[f.material,f.cor].filter(Boolean).join(" ")||f.nome}</h3><p>{f.marca||"Marca não informada"}</p></div></div>
        <div className="tool-item-metrics"><span>Disponível<strong>{peso(f).toLocaleString("pt-BR")} g</strong></span><span>Custo por kg<strong>{f.custo!=null?Number(f.custo).toLocaleString("pt-BR",{style:"currency",currency:"BRL"}):"Não informado"}</strong></span></div>
        {peso(f)<=100&&<p className="tool-stock-warning">Estoque baixo · planeje a reposição</p>}
        <div className="tool-actions"><ToolButton secondary onClick={()=>abrirSaida(f)}>Registrar saída</ToolButton><ToolButton secondary onClick={()=>edit(f)}>Editar</ToolButton><ToolButton danger aria-label={"Excluir "+(f.nome||f.cor)} onClick={()=>del(f.id)}>Excluir</ToolButton></div>
      </article>)}
    </div>
    {mostrarForm&&<ToolDialog compact data-tour="filamentos-form" title={editId?"Editar filamento":"Novo filamento"} subtitle="Organize seu estoque por marca, material e cor." icon="filamentos" color="#71e5ce" onClose={fecharForm} busy={salvando}>
      <div className="tool-form-grid"><MarcaSelector value={form.marca} onChange={v=>setForm({...form,marca:v})} marcas={marcasSalvas} onAddMarca={onAddMarca} /><Sel label="Material" value={form.material} onChange={e=>setForm({...form,material:e.target.value})}>{tiposMat.map(m=><option key={m}>{m}</option>)}</Sel><div className="tool-field-wide"><CorSelector value={form.cor} onChange={v=>setForm({...form,cor:v})} coresCustom={coresCustom} onAddCor={onAddCor} /></div><Inp label="Quantidade (g)" type="number" min="0" step="1" value={form.quantidade} onChange={e=>setForm({...form,quantidade:e.target.value})} placeholder="1000" /><Inp label="Custo por kg (R$)" type="number" min="0" value={form.custo} onChange={e=>setForm({...form,custo:e.target.value})} placeholder="Ex.: 90" /></div>
      <p className="tool-note">{editId?"A quantidade informada será o novo saldo deste filamento.":"Já tem esta marca, material e cor? A quantidade será somada ao cadastro existente."}</p>
      <div className="tool-actions"><ToolButton onClick={save} disabled={salvando}>{salvando?"Salvando…":editId?"Salvar alterações":"Adicionar filamento"}</ToolButton><ToolButton secondary onClick={fecharForm} disabled={salvando}>Cancelar</ToolButton></div>
    </ToolDialog>}
    {saidaAlvo&&<ToolDialog compact title="Registrar saída" subtitle={[saidaAlvo.marca,saidaAlvo.material,saidaAlvo.cor].filter(Boolean).join(" ")} icon="filamentos" color="#71e5ce" onClose={fecharSaida} busy={salvandoSaida}>
      <p className="tool-note">Estoque atual: {peso(saidaAlvo).toLocaleString("pt-BR")} g. Registre perdas, testes e usos fora dos pedidos.</p>
      <div className="tool-form-grid"><Inp label="Quantidade (g)" type="number" min="1" step="1" value={saidaForm.quantidade} onChange={e=>setSaidaForm({...saidaForm,quantidade:e.target.value})} placeholder="Ex.: 50" /><Sel label="Motivo" value={saidaForm.motivo} onChange={e=>setSaidaForm({...saidaForm,motivo:e.target.value})}>{motivosSaida.map(m=><option key={m}>{m}</option>)}</Sel><div className="tool-field-wide"><Inp label="Observação (opcional)" value={saidaForm.obs} onChange={e=>setSaidaForm({...saidaForm,obs:e.target.value})} placeholder="O que aconteceu?" /></div></div>
      <div className="tool-actions"><ToolButton onClick={confirmarSaida} disabled={salvandoSaida}>{salvandoSaida?"Registrando…":"Confirmar saída"}</ToolButton><ToolButton secondary onClick={fecharSaida} disabled={salvandoSaida}>Cancelar</ToolButton></div>
    </ToolDialog>}
  </div>;
}
